<template>
<div>
  <div class="title-bar title-h3">
    {{ title }} 티어 목록
  </div>
  <el-row class="title-bar" type="flex" justify="space-between">
    <div class="vertical-center-box">
      <el-button class="mr-r4" size="mini" type="primary" :disabled="!isDomain" @click="addTier">추가</el-button>
      <el-button size="mini" :disabled="!checkChangeFlag" @click="deleteTiers">삭제</el-button>
    </div>
    <div class="vertical-center-box">
      <w-header-search-button size="mini" @click="showSearchTool = !showSearchTool" />
    </div>
  </el-row>
  <el-row class="mr-b20">
    <el-table-wrapper v-loading="isLoading" ref="tierTable" :show-search-tool="showSearchTool" :total="total" row-class-name="c-dblclick"
    :data="dataList" :columns="dataColumns" :column-default="columnDefault" pagination :show-custom-header="true" @selection-change="tierSelectionChange" @cell-dblclick="onDbclickRow">
      <template slot-scope="scope" slot="apply-slot">
        <i v-if="scope.row.defaultApplyFlag == 'Y'" class="fas fa-circle text-mini state-normal"/>
        <i v-else class="fas fa-circle state-none" />
      </template>
    </el-table-wrapper>
  </el-row>
  <w-tier-setting-popup ref="tierSettingPopup" :popup-type="tierPopupType" @reloadData="reloadAllData" />
</div>
</template>

<script>
import { isEmpty, isNil } from 'lodash';
import { columnDefault } from '@/constants/table';
import { APM_OBJTYPE } from '@/constants/objdefine';
import { EMPTY_TEXT } from '@/utils/string';
import msgUtil from '@/utils/message';
import { convertThousandSeparatorFormat } from '@/utils/number';
import ApmSettingAPI from '@/api/settings/apm/apm-setting';
import WTierSettingPopup from '../popup/TierSettingPopup';

export default {
  name: 'WTierSettingView',
  components: {
    WTierSettingPopup,
  },
  props: {
    currentNode: {
      type: Object,
    },
    selectedType: String,
  },
  data() {
    return {
      columnDefault,
      selectedTiers: [],
      checkChangeFlag: false,
      showSearchTool: false,
      dataList: null,
      isLoading: false,
      total: 0,
      dataColumns: [
        {
          type: 'selection',
        },
        {
          prop: 'defaultApplyFlag',
          label: '기본',
          align: 'center',
          headerAlign: 'center',
          width: 60,
          scopedSlot: 'apply-slot',
        },
        {
          prop: 'tierName',
          label: '티어명',
          searchable: true,
          sortable: true,
        },
        {
          prop: 'txApdexTimeMillis',
          label: '트랜잭션 Apdex (ms)',
          searchable: true,
          sortable: true,
          formatter: this.getTableValueAfterCheckEmpty,
        },
        {
          prop: 'enduserApdexTimeMillis',
          label: '엔드유저 Apdex (ms)',
          searchable: true,
          sortable: true,
          formatter: this.getTableValueAfterCheckEmpty,
        },
      ],
      tierPopupType: null,
    };
  },
  computed: {
    isDomain() {
      return this.currentNode.type === 'domain';
    },
    title() {
      if (isEmpty(this.currentNode)) {
        return null;
      }
      return this.currentNode.domainId ? `[${this.currentNode.domainName}] ${APM_OBJTYPE[this.selectedType]}` : null;
    },
  },
  watch: {
    currentNode: {
      immediate: true,
      handler(currentNode) {
        if (isEmpty(currentNode) || currentNode.type !== 'domain') {
          return;
        }
        this.$nextTick(() => {
          this.loadTierList();
        });
      },
    },
  },
  methods: {
    getTableValueAfterCheckEmpty(row, column, value) {
      return isNil(value) ? EMPTY_TEXT : convertThousandSeparatorFormat(value);
    },
    /**
     * 티어 타입 및 도메인 아이디에 따라 티어를 조회한다.
     * @return {[type]} [description]
     * @author jda
     */
    loadTierList() {
      if (this.currentNode.type !== 'domain') {
        return;
      }

      this.isLoading = true;
      const tableParams = this.$refs.tierTable.getParameter();
      ApmSettingAPI.getTierList({ tierType: this.selectedType, domainId: this.currentNode.domainId, ...tableParams }).then((response) => {
        this.isLoading = false;
        if (response.data.success) {
          this.dataList = response.data.data;
          this.total = response.data.total;
        } else {
          this.dataList = [];
          this.total = 0;
        }
      });
    },
    /**
     * 삭제 할 티어 선택
     * @param  {[type]} row [description]
     * @return {[type]}     [description]
     * @author jda
     */
    tierSelectionChange(row) {
      this.selectedTiers = row;
      if (this.isDomain && row.length > 0) {
        this.checkChangeFlag = true;
      } else {
        this.checkChangeFlag = false;
      }
    },
    /**
     * 1. 티어를 삭제한다.
     * 2. 기본 티어는 서비스에서 삭제되지 않도록 분기 처리 하였음.
     * 3. 삭제 되는 티어 하위의 노드는 기본 티어의 하위로 옮겨짐.
     * @return {[type]} [description]
     * @author jda
     */
    deleteTiers() {
      this.$confirm('삭제하시겠습니까?<br/>(기본 티어는 삭제되지 않습니다.)', msgUtil.title.CONFIRM, {
        ...msgUtil.defaultConfirmButton,
        dangerouslyUseHTMLString: true,
      }).then(() => {
        ApmSettingAPI.deleteTier({ deletedList: this.selectedTiers, tierType: this.selectedType }).then((response) => {
          if (response.data.success) {
            this.reloadAllData();
          }
          this.$message(msgUtil.getResultMsgData(msgUtil.action.DELETE, '티어', response.data.success));
        }).catch(() => {
          this.$message(msgUtil.getResultMsgData(msgUtil.action.DELETE, '티어', false));
        });
      }).catch(() => {});
    },
    /**
     * 좌측 트리와 티어 리스트를 재조회한다.
     * @return {[type]} [description]
     * @author jda
     */
    reloadAllData() {
      this.loadTierList();
      this.$emit('reloadTree');
    },
    /**
     * 티어 변경 업창을 띄운다.
     * @param  {[type]} record [description]
     * @return {[type]}        [description]
     * @author jda
     */
    onDbclickRow(record) {
      this.tierPopupType = 'MODIFY';
      this.$refs.tierSettingPopup.open(record);
    },
    /**
     * 티어 추가 팝업창을 띄운다.
     * @author jda
     */
    addTier() {
      this.tierPopupType = 'ADD';
      const tierInfo = {
        tierType: this.selectedType,
        domainId: this.currentNode.domainId,
      };
      this.$refs.tierSettingPopup.open(tierInfo);
    },
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/views/settings/apm/ConfigSettings/components/view/TierSettingView.vue